import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminLayout from "../../../layouts/AdminLayout";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import PasswordField from "../../../components/ui/PasswordField";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import Button from "../../../components/ui/Button";
import Skeleton from "../../../components/ui/Skeleton";
import { User, Mail, Shield, ArrowLeft } from "lucide-react";
import { getSocieties } from "../../../services/societyService";
import { createUser } from "../../../services/userService";
import "./AddUser.css";

const AddAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [societies, setSocieties] = useState([]);

  const [form, setForm] = useState({
    society_id: "",
    full_name: "",
    email_id: "",
    mobile_number: "",
    username: "",
    password: "",
    system_role: "society-admin",
  });

  useEffect(() => {
    const loadSocieties = async () => {
      try {
        const data = await getSocieties();
        const list = data || [];
        setSocieties(list);
        if (list.length > 0) {
          setForm((prev) => ({ ...prev, society_id: String(list[0].society_id) }));
        }
      } catch (err) {
        console.error("Failed to load societies:", err);
        toast.error("Failed to load properties list");
      } finally {
        setLoading(false);
      }
    };
    loadSocieties();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Full name is required");
    if (!form.email_id.trim()) return toast.error("Email ID is required");
    if (!form.mobile_number.trim()) return toast.error("Mobile number is required");
    if (!form.password.trim()) return toast.error("Password is required");

    setSubmitting(true);
    try {
      await createUser({
        ...form,
        society_id: Number(form.society_id) || null,
        primary_role: "Admin",
      });
      toast.success("Administrator account created successfully");
      navigate("/society-admin");
    } catch (err) {
      console.error("CREATE ADMIN ERROR:", err);
      toast.error(err?.data?.message || err?.message || "Failed to create administrator");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Add Society Admin">
        <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
          <Skeleton variant="card" count={2} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Add Society Admin">
      <div className="add-user-page" style={{ maxWidth: "800px", margin: "0 auto", padding: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h1 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 4px 0" }}>Add Administrator</h1>
            <p style={{ fontSize: "13px", color: "var(--color-text-muted)", margin: 0 }}>
              Create an administrative user with property governance permissions.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={15} style={{ marginRight: "4px" }} /> Back
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="add-user-card" style={{ background: "var(--color-surface)", padding: "24px", borderRadius: "16px", border: "1px solid var(--color-border)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
            <FormField label="Assigned Property / Society" required>
              <SelectDropdown
                value={form.society_id}
                onChange={(e) => handleChange("society_id", e.target.value)}
                options={societies.map((s) => ({
                  label: `${s.society_name} (${s.society_type || "Residential"})`,
                  value: String(s.society_id),
                }))}
              />
            </FormField>

            <FormField label="System Role" required>
              <SelectDropdown
                value={form.system_role}
                onChange={(e) => handleChange("system_role", e.target.value)}
                options={[
                  { label: "Society Admin", value: "society-admin" },
                  { label: "Property Manager", value: "property-manager" },
                  { label: "Super Admin", value: "super-admin" },
                ]}
                leadIcon={<Shield size={15} />}
              />
            </FormField>

            <FormField label="Full Name" required>
              <TextInput
                value={form.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
                placeholder="e.g. John Doe"
                leadIcon={<User size={15} />}
              />
            </FormField>

            <FormField label="Email ID" required>
              <TextInput
                type="email"
                value={form.email_id}
                onChange={(e) => handleChange("email_id", e.target.value)}
                placeholder="admin@property.com"
                leadIcon={<Mail size={15} />}
              />
            </FormField>

            <FormField label="Mobile Number" required>
              <TextInput
                type="tel"
                value={form.mobile_number}
                onChange={(e) => handleChange("mobile_number", e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit mobile"
                prefixText="+91"
                maxLength={10}
              />
            </FormField>

            <FormField label="Username">
              <TextInput
                value={form.username}
                onChange={(e) => handleChange("username", e.target.value)}
                placeholder="Optional login username"
              />
            </FormField>

            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label="Account Password" required helperText="Temporary password for initial login.">
                <PasswordField
                  value={form.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder="Enter strong password"
                />
              </FormField>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "24px", paddingTop: "16px", borderTop: "1px solid var(--color-border)" }}>
            <Button variant="outline" type="button" onClick={() => navigate(-1)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              Create Administrator
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddAdmin;
